import React, { useEffect } from 'react';
import './Legal.css';

const CONTACT = 'xoxo@inflection.pro';
const UPDATED = '15 September 2026';

const Section = ({ title, children }) => (
  <section className="legal-section">
    <h2>{title}</h2>
    {children}
  </section>
);

// Both documents live in one file because they share a shell and are read
// perhaps twice a year - splitting them would cost more than it saves.
const Legal = ({ doc }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc]);

  return (
    <div className="legal-page">
      <div className="legal-inner">
        {doc === 'privacy' ? <Privacy /> : <Terms />}
        <p className="legal-updated">Last updated {UPDATED}.</p>
      </div>
    </div>
  );
};

const Privacy = () => (
  <>
    <h1>Privacy Policy</h1>
    <p className="legal-lede">
      Inflection is a research tool for public-market demand signals. This policy explains
      what we collect when you use it, why, and what we do not do with it.
    </p>

    <Section title="What we collect">
      <p>
        When you create an account we collect your email address, and your name and profile
        image if you sign in with Google. Authentication is handled by Clerk, Inc., which
        stores your credentials on our behalf — we never see or store your password.
      </p>
      <p>
        We also record ordinary technical data as you use the site: pages viewed, approximate
        location derived from your IP address, browser and device type, and timestamps. This
        is used to keep the service running and to understand which parts of it get used.
      </p>
    </Section>

    <Section title="What we do not collect">
      <p>
        We do not ask for, receive, or store payment card details, brokerage credentials,
        portfolio holdings, or any information about trades you make. We do not buy personal
        data about you from third parties.
      </p>
    </Section>

    <Section title="How we use it">
      <p>
        Your email is used to sign you in, to reply when you contact us, and to send occasional
        messages about the service. We do not sell your personal information, and we do not
        share it with advertisers.
      </p>
      <p>
        The market data shown in Inflection is aggregate and public — search interest, company
        filings, price history. It is not derived from you or from other users.
      </p>
    </Section>

    <Section title="Who we share it with">
      <p>
        We use a small number of processors to run the service: Clerk for authentication,
        Vercel for hosting the website, Render for the API, and Neon for the database. Each
        handles data only to provide its service to us. We will also disclose information
        where we are legally required to.
      </p>
    </Section>

    <Section title="Cookies">
      <p>
        We set cookies that are necessary for the site to work — principally the session
        cookie that keeps you signed in. We do not use advertising or cross-site tracking
        cookies.
      </p>
    </Section>

    <Section title="Retention and deletion">
      <p>
        We keep your account data for as long as your account exists. Email{' '}
        <a href={`mailto:${CONTACT}`}>{CONTACT}</a> asking for deletion and we will remove
        your account and associated personal data, ordinarily within 30 days.
      </p>
    </Section>

    <Section title="Your rights">
      <p>
        Depending on where you live, you may have the right to access, correct, export, or
        delete the personal information we hold about you, and to object to certain
        processing. Write to <a href={`mailto:${CONTACT}`}>{CONTACT}</a> and we will act on
        the request.
      </p>
    </Section>

    <Section title="Children">
      <p>
        Inflection is not intended for anyone under 18 and we do not knowingly collect
        information from children.
      </p>
    </Section>

    <Section title="Changes and contact">
      <p>
        If this policy changes materially we will post the revision here and update the date
        below. Questions go to <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
      </p>
    </Section>
  </>
);

const Terms = () => (
  <>
    <h1>Terms of Service</h1>
    <p className="legal-lede">
      These terms govern your use of Inflection. By creating an account or using the site,
      you agree to them.
    </p>

    <Section title="Not investment advice">
      <p>
        <strong>
          Inflection is a research and information tool. Nothing on it is investment,
          financial, legal, or tax advice, and nothing on it is a recommendation to buy or
          sell any security.
        </strong>{' '}
        We are not a broker-dealer, investment adviser, or financial planner, and no part of
        the service is personalised to your circumstances.
      </p>
      <p>
        Trend signals are statistical observations about public search behaviour. They are
        frequently wrong about what a company's revenue or share price will do. Any decision
        you make after reading them is yours alone, and you bear the full risk of loss.
      </p>
    </Section>

    <Section title="Your account">
      <p>
        You must be at least 18 and provide accurate information when registering. You are
        responsible for activity under your account and for keeping your sign-in method
        secure. Tell us promptly at <a href={`mailto:${CONTACT}`}>{CONTACT}</a> if you believe
        your account has been compromised.
      </p>
    </Section>

    <Section title="Acceptable use">
      <p>You agree not to:</p>
      <ul>
        <li>scrape, crawl, or bulk-download the service, or use it through automated means;</li>
        <li>resell, redistribute, or publish our data as your own product;</li>
        <li>share your account credentials with anyone else;</li>
        <li>attempt to breach, probe, or disrupt the service or its infrastructure;</li>
        <li>use the service in breach of any applicable law or market-conduct rule.</li>
      </ul>
    </Section>

    <Section title="Our content">
      <p>
        The site, its analysis, scoring methodology, text, and design are owned by Inflection
        and protected by copyright. You may use the output for your own internal research.
        Underlying market and search data belongs to its respective providers.
      </p>
    </Section>

    <Section title="Availability">
      <p>
        The service is provided as-is and as-available. We do not promise it will be
        uninterrupted, error-free, timely, or complete, and we may change, suspend, or
        discontinue any part of it at any time. Data may be delayed, incomplete, or incorrect.
      </p>
    </Section>

    <Section title="Limitation of liability">
      <p>
        To the fullest extent permitted by law, Inflection is not liable for any trading or
        investment losses, lost profits, or indirect, incidental, special, or consequential
        damages arising from your use of the service. Where liability cannot be excluded, it
        is limited to the greater of the amount you paid us in the preceding twelve months or
        one hundred Australian dollars.
      </p>
      <p>
        Nothing in these terms excludes rights you have under consumer law that cannot
        lawfully be excluded.
      </p>
    </Section>

    <Section title="Termination">
      <p>
        You may close your account at any time. We may suspend or terminate access if you
        breach these terms or use the service in a way that risks harm to us or other users.
      </p>
    </Section>

    <Section title="Changes">
      <p>
        We may update these terms. Continued use after a revision is posted means you accept
        the updated terms.
      </p>
    </Section>

    <Section title="Governing law">
      <p>
        These terms are governed by the laws of New South Wales, Australia, and the courts of
        that state have exclusive jurisdiction. Questions go to{' '}
        <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
      </p>
    </Section>
  </>
);

export default Legal;
